import React, { useMemo, useState } from 'react';
import Dialog from './Dialog';
import AppIcon from './AppIcon';
import { normalizeWebsiteUrl, validateUrl } from './AppDialog';
import useDialogMutation from '../hooks/useDialogMutation';

function QuickLaunchPickerDialog({
  apps,
  quickLaunchIds,
  onAdd,
  onCreate,
  onDelete,
  onSaveForLater,
  onClose
}) {
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [errors, setErrors] = useState({});
  const { run, saving, error: saveError } = useDialogMutation();
  const available = useMemo(
    () => apps.filter((app) => !quickLaunchIds.includes(app.id)),
    [apps, quickLaunchIds]
  );
  const alreadyAdded = useMemo(
    () => apps.filter((app) => quickLaunchIds.includes(app.id)),
    [apps, quickLaunchIds]
  );

  const submit = async (event) => {
    event.preventDefault();
    const next = {};
    if (!name.trim()) next.name = 'Application name is required.';
    const normalizedUrl = normalizeWebsiteUrl(url);
    const urlError = validateUrl(normalizedUrl);
    if (urlError) next.url = urlError;
    setErrors(next);
    if (Object.keys(next).length || saving) return;
    await run(() => onCreate({ name: name.trim(), url: normalizedUrl }), onClose);
  };

  const saveForLaterButton = (app) => (
    <button
      type="button"
      className="quick-launch-picker-vault"
      onClick={() => onSaveForLater(app)}
      disabled={saving}
    >
      Save for later
    </button>
  );

  return (
    <Dialog title="Add to Quick Launch" onClose={onClose} locked={saving}>
      <div className="quick-launch-picker-content">
        {creating ? (
          <form onSubmit={submit} noValidate>
            <label className="dialog-label" htmlFor="quick-app-name">Application Name</label>
            <input
              id="quick-app-name"
              className="dialog-input"
              value={name}
              maxLength={40}
              autoFocus
              disabled={saving}
              aria-invalid={Boolean(errors.name)}
              onChange={(event) => setName(event.target.value)}
            />
            {errors.name && <p className="dialog-error">{errors.name}</p>}
            <label className="dialog-label field-gap" htmlFor="quick-app-url">Website URL</label>
            <input
              id="quick-app-url"
              className="dialog-input"
              type="url"
              value={url}
              placeholder="example.com/page"
              disabled={saving}
              aria-invalid={Boolean(errors.url)}
              onChange={(event) => setUrl(event.target.value)}
            />
            {errors.url && <p className="dialog-error">{errors.url}</p>}
            {saveError && <p className="dialog-error" role="alert">{saveError}</p>}
            <div className="dialog-actions">
              <button type="button" className="btn" onClick={() => setCreating(false)} disabled={saving}>Back</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>Add</button>
            </div>
          </form>
        ) : (
          <>
            <div className="quick-launch-picker-scroll">
              <div className="quick-launch-picker-list">
                {available.map((app) => (
                  <div className="quick-launch-picker-row" key={app.id}>
                    <button
                      type="button"
                      className="quick-launch-picker-item"
                      onClick={() => run(() => onAdd(app.id), onClose)}
                      disabled={saving}
                    >
                      <AppIcon app={app} />
                      <span>{app.name}</span>
                      <span className="quick-launch-picker-add">+</span>
                    </button>
                    {!app.sectionId && saveForLaterButton(app)}
                    {!app.sectionId && (
                      <button
                        type="button"
                        className="quick-launch-picker-delete"
                        onClick={() => onDelete(app)}
                        disabled={saving}
                        aria-label={`Delete ${app.name}`}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                ))}
                {available.length === 0 && (
                  <p className="quick-launch-picker-empty">All saved applications are in Quick Launch.</p>
                )}
              </div>
              {alreadyAdded.length > 0 && (
                <div className="quick-launch-already-added">
                  <p className="quick-launch-picker-heading">Already in Quick Launch</p>
                  <div className="quick-launch-picker-list">
                    {alreadyAdded.map((app) => (
                      <div className="quick-launch-picker-row" key={app.id}>
                        <div className="quick-launch-picker-item is-added">
                          <AppIcon app={app} />
                          <span>{app.name}</span>
                          <span className="quick-launch-picker-add">Added</span>
                        </div>
                        {!app.sectionId && saveForLaterButton(app)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {saveError && <p className="dialog-error" role="alert">{saveError}</p>}
            <div className="dialog-actions">
              <button type="button" className="btn" onClick={onClose} disabled={saving}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={() => setCreating(true)} disabled={saving}>
                Create new application
              </button>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}

export default QuickLaunchPickerDialog;